import { mm } from "@inovitas/infra3dsdk";
import { WarningAmberOutlined } from "@mui/icons-material";
import { FeatureCollection } from "geojson";
import React from "react";

import { FeatureColumn } from "shared/ui/toolbar/tools/layertreeAndEdit/featureTable/FeatureListTable";
import {
  LineSymbol,
  MultiLineSymbol,
  MultiPointSymbol,
  MultiPolygonSymbol,
  PointSymbol,
  PolygonSymbol,
} from "shared/common/utils/icons/icons";

export interface Sorter {
  property: string;
  direction: "ASC" | "DESC";
}

export const getIconForGeomType = (
  icon: string | mm.GEOMETRYTYPE | undefined
): JSX.Element => {
  switch (icon) {
    case mm.GEOMETRYTYPE.LINESTRING:
    case "linestring":
    case "LineString":
      return <LineSymbol />;
    case mm.GEOMETRYTYPE.MULTILINESTRING:
    case "multilinestring":
    case "MultiLineString":
      return <MultiLineSymbol />;
    case mm.GEOMETRYTYPE.POLYGON:
    case "polygon":
    case "Polygon":
      return <PolygonSymbol />;
    case mm.GEOMETRYTYPE.MULTIPOLYGON:
    case "multipolygon":
    case "MultiPolygon":
      return <MultiPolygonSymbol />;
    case mm.GEOMETRYTYPE.POINT:
    case "point":
    case "Point":
      return <PointSymbol />;
    case mm.GEOMETRYTYPE.MULTIPOINT:
    case "multipoint":
    case "MultiPoint":
      return <MultiPointSymbol />;
    default:
      return <WarningAmberOutlined sx={{ color: "#ff6700" }} />;
  }
};

export const getGeometryTypeEnum = (
  name: string | mm.GEOMETRYTYPE | undefined
): number => {
  switch (name) {
    case mm.GEOMETRYTYPE.LINESTRING:
    case "linestring":
    case "LineString":
      return mm.GEOMETRYTYPE.LINESTRING;
    case mm.GEOMETRYTYPE.MULTILINESTRING:
    case "multilinestring":
    case "MultiLineString":
      return mm.GEOMETRYTYPE.MULTILINESTRING;
    case mm.GEOMETRYTYPE.POLYGON:
    case "polygon":
    case "Polygon":
      return mm.GEOMETRYTYPE.POLYGON;
    case mm.GEOMETRYTYPE.MULTIPOLYGON:
    case "multipolygon":
    case "MultiPolygon":
      return mm.GEOMETRYTYPE.MULTIPOLYGON;
    case mm.GEOMETRYTYPE.POINT:
    case "point":
    case "Point":
      return mm.GEOMETRYTYPE.POINT;
    case mm.GEOMETRYTYPE.MULTIPOINT:
    case "multipoint":
    case "MultiPoint":
      return mm.GEOMETRYTYPE.MULTIPOINT;
    default:
      return mm.GEOMETRYTYPE.UNDEFINED;
  }
};

export const getFeaturesCountFromLayer = (layer: mm.Layer): Promise<number> => {
  return new Promise((resolve, reject) => {
    layer.exportFeatures(
      (count) => {
        resolve(count);
        return {};
      },
      (error: string) => {
        reject(error);
        return {};
      },
      {
        countonly: true,
      }
    );
  });
};

export const getFeatureCollectionFromLayer = (
  layer: mm.Layer,
  start?: number,
  limit?: number,
  sorters?: Sorter[],
  srid?: number
): Promise<FeatureCollection> => {
  return new Promise((resolve, reject) => {
    layer.exportFeatures(
      (featureCollection: FeatureCollection) => {
        resolve(featureCollection);
        return {};
      },
      (error: string) => {
        reject(error);
        return {};
      },
      {
        start: start || 0,
        limit: limit || undefined,
        srid: srid || layer.scene.getCurrentGeoframe().srid,
        sorters: sorters || [],
      }
    );
  });
};

export const getColumnDefinitionsFromLayer = (
  layer: mm.Layer
): FeatureColumn[] => {
  const columns: FeatureColumn[] = [];
  columns.push({
    id: 0,
    key: "id",
    name: "ID",
    datatype: mm.LAYERPROPERTYTYPE.INTEGER,
    editable: false,
  });
  if (layer.propertydef.length > 0) {
    layer.propertydef.forEach((item, index: number) => {
      if (item.key !== "id") {
        columns.push({
          id: index + 1,
          ...item,
        });
      }
    });
  }
  return columns;
};
